import { useCallback, useId } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import type {
  IFormFieldProps,
  IMultiSelectProps,
  ISelectOption,
  ISingleSelectProps,
} from "@/ui-kit";
import { FormField, Select } from "@/ui-kit";
import { get } from "../utils";

type ISelectOmitProps = "label" | "value" | "onChange";

type BaseSelectControlProps = Partial<IFormFieldProps> & {
  name: string;
  formElementClass?: string;
  clear?: boolean;
};

export type SingleSelectControlProps = Omit<
  ISingleSelectProps,
  ISelectOmitProps
> &
  BaseSelectControlProps & {
    onChange?: (value: string | undefined, item?: ISelectOption) => void;
  };

export type MultiSelectControlProps = Omit<
  IMultiSelectProps,
  ISelectOmitProps
> &
  BaseSelectControlProps & {
    onChange?: (value: string[], item?: ISelectOption) => void;
  };

type Props = SingleSelectControlProps | MultiSelectControlProps;

export const SelectControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    onChange,
    clear,
    description,
    ...restSelectProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;

  // Select has no native label: it is named by the FormField label's id.
  const labelId = useId();
  const ariaLabelledby =
    restSelectProps.ariaLabelledby ??
    (label && !restSelectProps.ariaLabel ? labelId : undefined);

  const handleChange = useCallback(
    (onFieldChange: (...event: any[]) => void) => {
      return (value: string | string[] | undefined, item?: ISelectOption) => {
        if (onChange) {
          onChange(value as never, item);
        }
        onFieldChange(value);
      };
    },
    [onChange],
  );

  const selectElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          const clearValue = restSelectProps.multiple ? [] : undefined;
          onFieldChange(clearValue);
          if (onChange) {
            onChange(clearValue as never);
          }
        };

        return (
          <Select
            {...restSelectProps}
            ariaLabelledby={ariaLabelledby}
            onChange={handleChange(onFieldChange)}
            value={value}
            invalid={invalid || !!errorMessage}
            onClear={
              restSelectProps.onClear || (clear ? handleClear : undefined)
            }
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      labelId={labelId}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
    >
      {selectElement}
    </FormField>
  );
};
